import { Check, FileText, RectangleHorizontal } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

type FooterAlignment = 'left' | 'center' | 'right';

export interface LayoutSettings {
  pageSize: 'a4' | 'a5' | 'letter';
  orientation: 'portrait' | 'landscape';
  margins: 'normal' | 'narrow' | 'wide';
  headerLayout: {
    left: string[];
    center: string[];
    right: string[];
    showDivider: boolean;
    dividerThickness?: string;
    dividerColor?: string;
    repeatOnAllPages: boolean;
  };
  footer: {
    enabled: boolean;
    alignment: FooterAlignment;
    showCompanyContact: boolean;
    showCompanyReg: boolean;
    showVatNumber: boolean;
    showDivider: boolean;
    dividerThickness?: string;
    dividerColor?: string;
    repeatOnAllPages: boolean;
  };
  pageNumbers: {
    enabled: boolean;
    position: 'bottom-center' | 'bottom-left' | 'bottom-right' | 'top-right';
    format: 'page-of' | 'slash' | 'number';
  };
}

interface LayoutTabProps {
  settings: LayoutSettings;
  onChange: (settings: LayoutSettings) => void;
}

export function LayoutTab({ settings, onChange }: Readonly<LayoutTabProps>) {
  const headerOptions = [
    { value: 'logo', label: 'Logo' },
    { value: 'company-name', label: 'Company Name' },
    { value: 'company-address', label: 'Company Address' },
    { value: 'empty', label: 'Empty' },
  ];

  type HeaderZone = 'left' | 'center' | 'right';
  const getAvailableOptions = (currentZone: HeaderZone) => {
    const otherZones = (['left', 'center', 'right'] as HeaderZone[]).filter((z) => z !== currentZone);
    const selectedInOtherZones = otherZones.flatMap((zone) => settings.headerLayout[zone]);
    return headerOptions.filter((opt) => opt.value === 'empty' || !selectedInOtherZones.includes(opt.value));
  };

  const toggleItemInZone = (zone: HeaderZone, value: string) => {
    const currentValues = settings.headerLayout[zone];
    let newValues: string[];
    if (value === 'empty') {
      newValues = ['empty'];
    } else {
      const withoutEmpty = currentValues.filter((v) => v !== 'empty');
      if (currentValues.includes(value)) {
        newValues = withoutEmpty.filter((v) => v !== value);
        if (newValues.length === 0) newValues = ['empty'];
      } else {
        newValues = [...withoutEmpty, value];
      }
    }
    onChange({
      ...settings,
      headerLayout: { ...settings.headerLayout, [zone]: newValues },
    });
  };

  return (
    <div className="space-y-4 p-4">
      {/* Panel 1: Page Setup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Page Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="space-y-2">
            <Label>Page Size</Label>
            <Select
              value={settings.pageSize}
              onValueChange={(value: LayoutSettings['pageSize']) => onChange({ ...settings, pageSize: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="a4">A4</SelectItem>
                <SelectItem value="a5">A5</SelectItem>
                <SelectItem value="letter">Letter</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">A4 is recommended for UK business documents</p>
          </div>

          <div className="space-y-2">
            <Label>Orientation</Label>
            <ToggleGroup
              type="single"
              value={settings.orientation}
              onValueChange={(value: LayoutSettings['orientation']) => {
                if (value) onChange({ ...settings, orientation: value });
              }}
              className="justify-start"
            >
              <ToggleGroupItem value="portrait" className="gap-2">
                <FileText className="h-4 w-4" />
                Portrait
              </ToggleGroupItem>
              <ToggleGroupItem value="landscape" className="gap-2">
                <RectangleHorizontal className="h-4 w-4" />
                Landscape
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="space-y-2">
            <Label>Margins</Label>
            <Select
              value={settings.margins}
              onValueChange={(value: LayoutSettings['margins']) => onChange({ ...settings, margins: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal 25mm</SelectItem>
                <SelectItem value="narrow">Narrow 15mm</SelectItem>
                <SelectItem value="wide">Wide 35mm</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Panel 2: Header Layout */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Header Layout</CardTitle>
          <CardDescription>Choose what appears in each zone of your document header</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Zone pickers */}
          <div className="grid grid-cols-3 gap-2">
            {(['left', 'center', 'right'] as const).map((zone) => {
              const zoneLabel = zone === 'center' ? 'Centre' : zone.charAt(0).toUpperCase() + zone.slice(1);
              const contentOptions = headerOptions.filter((opt) => opt.value !== 'empty');
              return (
                <div key={zone} className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {zoneLabel}
                  </Label>
                  <div className="space-y-1">
                    {contentOptions.map((opt) => {
                      const isSelected = settings.headerLayout[zone].includes(opt.value);
                      const isUsedElsewhere = (['left', 'center', 'right'] as HeaderZone[])
                        .filter((z) => z !== zone)
                        .some((z) => settings.headerLayout[z].includes(opt.value));
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={isUsedElsewhere}
                          onClick={() => toggleItemInZone(zone, opt.value)}
                          className={cn(
                            'w-full text-left text-xs px-2.5 py-2 rounded-md border transition-all',
                            'flex items-center justify-between gap-1',
                            isSelected
                              ? 'bg-primary/10 border-primary text-primary font-medium'
                              : isUsedElsewhere
                                ? 'bg-muted/50 border-border text-muted-foreground opacity-40 cursor-not-allowed'
                                : 'bg-background border-border text-foreground hover:bg-muted hover:border-muted-foreground/40',
                          )}
                        >
                          <span>{opt.label}</span>
                          {isSelected && <Check className="h-3 w-3 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            Select items for each zone. An item selected in one zone is unavailable in others.
          </p>

          {/* Header Divider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Show divider line below header</Label>
              <Switch
                checked={settings.headerLayout.showDivider}
                onCheckedChange={(checked) =>
                  onChange({
                    ...settings,
                    headerLayout: {
                      ...settings.headerLayout,
                      showDivider: checked,
                    },
                  })
                }
              />
            </div>

            {settings.headerLayout.showDivider && (
              <div className="space-y-4 pl-4 border-l-2 border-muted">
                <div className="space-y-2">
                  <Label>Divider Thickness</Label>
                  <Select
                    value={settings.headerLayout.dividerThickness || '1'}
                    onValueChange={(value) =>
                      onChange({
                        ...settings,
                        headerLayout: {
                          ...settings.headerLayout,
                          dividerThickness: value,
                        },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Thin (1px)</SelectItem>
                      <SelectItem value="2">Medium (2px)</SelectItem>
                      <SelectItem value="3">Thick (3px)</SelectItem>
                      <SelectItem value="4">Extra Thick (4px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Divider Colour</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-shrink-0">
                      <input
                        type="color"
                        value={settings.headerLayout.dividerColor || '#e5e7eb'}
                        onChange={(e) =>
                          onChange({
                            ...settings,
                            headerLayout: {
                              ...settings.headerLayout,
                              dividerColor: e.target.value,
                            },
                          })
                        }
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                    </div>
                    <Input
                      type="text"
                      value={settings.headerLayout.dividerColor || '#e5e7eb'}
                      onChange={(e) =>
                        onChange({
                          ...settings,
                          headerLayout: {
                            ...settings.headerLayout,
                            dividerColor: e.target.value,
                          },
                        })
                      }
                      placeholder="#e5e7eb"
                      className="font-mono"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Default uses a light gray color</p>
                </div>
              </div>
            )}
          </div>

          {/* Header Repeat */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Repeat header on all pages</Label>
              <p className="text-xs text-muted-foreground mt-1">Recommended for multi-page documents</p>
            </div>
            <Switch
              checked={settings.headerLayout.repeatOnAllPages}
              onCheckedChange={(checked) =>
                onChange({
                  ...settings,
                  headerLayout: {
                    ...settings.headerLayout,
                    repeatOnAllPages: checked,
                  },
                })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Panel 3: Footer Layout */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Footer Layout</CardTitle>
            <Switch
              checked={settings.footer.enabled}
              onCheckedChange={(checked) =>
                onChange({
                  ...settings,
                  footer: { ...settings.footer, enabled: checked },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.footer.enabled && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <Label>Content Alignment</Label>
              <div className="grid grid-cols-3 gap-2">
                {(['left', 'center', 'right'] as const).map((align) => {
                  const label = align === 'center' ? 'Centre' : align.charAt(0).toUpperCase() + align.slice(1);
                  const isSelected = (settings.footer.alignment ?? 'center') === align;
                  return (
                    <button
                      key={align}
                      type="button"
                      onClick={() =>
                        onChange({
                          ...settings,
                          footer: { ...settings.footer, alignment: align },
                        })
                      }
                      className={cn(
                        'text-xs px-2.5 py-2 rounded-md border transition-all flex items-center justify-between gap-1',
                        isSelected
                          ? 'bg-primary/10 border-primary text-primary font-medium'
                          : 'bg-background border-border text-foreground hover:bg-muted hover:border-muted-foreground/40',
                      )}
                    >
                      <span>{label}</span>
                      {isSelected && <Check className="h-3 w-3 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Applies to all footer content as a single block</p>
            </div>

            <div className="flex items-center justify-between">
              <Label>Show company contact details</Label>
              <Switch
                checked={settings.footer.showCompanyContact}
                onCheckedChange={(checked) =>
                  onChange({
                    ...settings,
                    footer: { ...settings.footer, showCompanyContact: checked },
                  })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show company registration number</Label>
              <Switch
                checked={settings.footer.showCompanyReg}
                onCheckedChange={(checked) =>
                  onChange({
                    ...settings,
                    footer: { ...settings.footer, showCompanyReg: checked },
                  })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show VAT registration number</Label>
              <Switch
                checked={settings.footer.showVatNumber}
                onCheckedChange={(checked) =>
                  onChange({
                    ...settings,
                    footer: { ...settings.footer, showVatNumber: checked },
                  })
                }
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Show divider line above footer</Label>
                <Switch
                  checked={settings.footer.showDivider}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...settings,
                      footer: { ...settings.footer, showDivider: checked },
                    })
                  }
                />
              </div>

              {settings.footer.showDivider && (
                <div className="space-y-4 pl-4 border-l-2 border-muted">
                  <div className="space-y-2">
                    <Label>Divider Thickness</Label>
                    <Select
                      value={settings.footer.dividerThickness || '1'}
                      onValueChange={(value) =>
                        onChange({
                          ...settings,
                          footer: {
                            ...settings.footer,
                            dividerThickness: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Thin (1px)</SelectItem>
                        <SelectItem value="2">Medium (2px)</SelectItem>
                        <SelectItem value="3">Thick (3px)</SelectItem>
                        <SelectItem value="4">Extra Thick (4px)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Divider Colour</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-shrink-0">
                        <input
                          type="color"
                          value={settings.footer.dividerColor || '#e5e7eb'}
                          onChange={(e) =>
                            onChange({
                              ...settings,
                              footer: {
                                ...settings.footer,
                                dividerColor: e.target.value,
                              },
                            })
                          }
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                      </div>
                      <Input
                        type="text"
                        value={settings.footer.dividerColor || '#e5e7eb'}
                        onChange={(e) =>
                          onChange({
                            ...settings,
                            footer: {
                              ...settings.footer,
                              dividerColor: e.target.value,
                            },
                          })
                        }
                        placeholder="#e5e7eb"
                        className="font-mono"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Default uses a light gray color</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label>Repeat footer on all pages</Label>
              <Switch
                checked={settings.footer.repeatOnAllPages}
                onCheckedChange={(checked) =>
                  onChange({
                    ...settings,
                    footer: { ...settings.footer, repeatOnAllPages: checked },
                  })
                }
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Panel 4: Page Numbers */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Page Numbers</CardTitle>
            <Switch
              checked={settings.pageNumbers.enabled}
              onCheckedChange={(checked) =>
                onChange({
                  ...settings,
                  pageNumbers: { ...settings.pageNumbers, enabled: checked },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.pageNumbers.enabled && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <Label>Position</Label>
              <Select
                value={settings.pageNumbers.position}
                onValueChange={(value: LayoutSettings['pageNumbers']['position']) =>
                  onChange({
                    ...settings,
                    pageNumbers: { ...settings.pageNumbers, position: value },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom-center">Bottom centre</SelectItem>
                  <SelectItem value="bottom-left">Bottom left</SelectItem>
                  <SelectItem value="bottom-right">Bottom right</SelectItem>
                  <SelectItem value="top-right">Top right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Format</Label>
              <Select
                value={settings.pageNumbers.format}
                onValueChange={(value: LayoutSettings['pageNumbers']['format']) =>
                  onChange({
                    ...settings,
                    pageNumbers: { ...settings.pageNumbers, format: value },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="page-of">Page 1 of 3</SelectItem>
                  <SelectItem value="slash">1 / 3</SelectItem>
                  <SelectItem value="number">1</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
